/**
 * UserPremiumPackageRemapper
 *
 * Maps between the data and domain layers for the current user's premium
 * packages:
 *   - UserPremiumPackageStorage (SecureStorage) → Entity
 *   - UserPremiumPackageDto    (Firestore wire) → Entity (via fromJson)
 */
import type { UserPremiumPackageDto } from '@/data/dtos/userPremiumPackage/userPremiumPackageDto';
import type { JsonObject } from '@/data/types/json';
import type { UserPremiumPackageStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserPremiumPackageEntity } from '@/domain/entities/userPremiumPackage/userPremiumPackageEntity';

export class UserPremiumPackageRemapper {
  static toStorage(dto: UserPremiumPackageDto): UserPremiumPackageStorage {
    return {
      productId: dto.productId,
      name: dto.name,
      status: dto.status,
      transactionId: dto.transactionId,
      originalTransactionId: dto.originalTransactionId,
      createdDate: dto.createdDate,
      updatedDate: dto.updatedDate,
      expireDate: dto.expireDate,
      actions: dto.actions,
      active: dto.active,
      platform: dto.platform,
      verificationStatus: dto.verificationStatus,
      identityVerificationId: dto.identityVerificationId,
    };
  }

  static fromStorage(storage: UserPremiumPackageStorage): UserPremiumPackageEntity {
    return {
      productId: storage.productId,
      name: storage.name,
      status: storage.status,
      transactionId: storage.transactionId,
      originalTransactionId: storage.originalTransactionId,
      createdDate: storage.createdDate,
      updatedDate: storage.updatedDate,
      expireDate: storage.expireDate,
      actions: storage.actions,
      active: storage.active,
      platform: storage.platform,
      verificationStatus: storage.verificationStatus,
      identityVerificationId: storage.identityVerificationId,
    };
  }

  static fromStorageList(storageList: UserPremiumPackageStorage[]): UserPremiumPackageEntity[] {
    return storageList.map(UserPremiumPackageRemapper.fromStorage);
  }

  /**
   * Defensive parse from raw Firestore `JsonObject` to a typed DTO.
   * Wire field names are pure camelCase — no `@JsonKey` aliases on the
   * Flutter model (lib/data/models/user_premium_package.dart:10-46).
   * `createdDate` has a Dart default of 0; nullable fields pass through.
   */
  static fromJson(data: JsonObject): UserPremiumPackageDto {
    return {
      productId: (data['productId'] as string | undefined) ?? '',
      name: (data['name'] as string | undefined) ?? '',
      status: (data['status'] as number | undefined) ?? 0,
      transactionId: data['transactionId'] as string | undefined,
      originalTransactionId: data['originalTransactionId'] as string | undefined,
      createdDate: (data['createdDate'] as number | undefined) ?? 0,
      updatedDate: data['updatedDate'] as number | undefined,
      expireDate: data['expireDate'] as number | undefined,
      actions: (data['actions'] as number[] | undefined) ?? [],
      active: (data['active'] as boolean | undefined) ?? false,
      platform: (data['platform'] as number | undefined) ?? 0,
      verificationStatus: data['verificationStatus'] as number | undefined,
      identityVerificationId: data['identityVerificationId'] as string | undefined,
    };
  }

  static fromJsonList(dataList: JsonObject[]): UserPremiumPackageDto[] {
    return dataList.map(UserPremiumPackageRemapper.fromJson);
  }
}
